import { useNavigate } from "react-router-dom";
import { useState, useRef } from "react";
import "./style.css";
import "../../../styles/styleGlobal.css";
import { jwtDecode } from "jwt-decode";
import {
  API_ENDPOINTS,
  API_BASE_URL,
  limparSessaoEstudante,
} from "../../../js/api.js";

const Inicial = () => {
  const navigate = useNavigate();
  // const [cpf, setCpf] = useState('');
  const [usuario, setUsuario] = useState("");
  const [senha, setSenha] = useState("");
  // const [codigoempresa, setCodigoempresa] = useState('');
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  // Flag compartilhada entre as tentativas de login em paralelo:
  // quando uma delas navega (sucesso), a outra DEVE abortar antes de
  // sobrescrever token/localStorage/navigate. Sem isso, o /tokens
  // rodando em paralelo pode sobrescrever o token do /tce/login-estudante
  // e redirecionar pra /tce sem o ?id=86 — quebrando o fluxo do estudante
  // (cairia em "Nenhuma autorização vinculada").
  const navigatedRef = useRef(false);

  function validarCPF(cpf) {
    cpf = cpf.replace(/\D/g, "");
    if (cpf.length !== 11 || /^(\d)\1{10}$/.test(cpf)) return false;

    let soma = 0,
      resto;
    for (let i = 0; i < 9; i++) soma += parseInt(cpf.charAt(i)) * (10 - i);
    resto = (soma * 10) % 11;
    if (resto === 10 || resto === 11) resto = 0;
    if (resto !== parseInt(cpf.charAt(9))) return false;

    soma = 0;
    for (let i = 0; i < 10; i++) soma += parseInt(cpf.charAt(i)) * (11 - i);
    resto = (soma * 10) % 11;
    if (resto === 10 || resto === 11) resto = 0;
    if (resto !== parseInt(cpf.charAt(10))) return false;

    return true;
  }

  const formatUsuario = (value) => {
    const digits = value.replace(/\D/g, "");

    if (digits.length <= 6) {
      return digits;
    }

    let cpf = digits.slice(0, 11);

    cpf = cpf.replace(/(\d{3})(\d)/, "$1.$2");
    cpf = cpf.replace(/(\d{3})(\d)/, "$1.$2");
    cpf = cpf.replace(/(\d{3})(\d{1,2})$/, "$1-$2");

    return cpf;
  };

  const handleUsuarioChange = (e) => {
    setUsuario(formatUsuario(e.target.value));
  };

  // Tenta autenticar o estudante via um endpoint dedicado (TCA ou TCE).
  //
  // Comportamento esperado: o endpoint foi feito pra ser usado apenas
  // por estudantes (nível 1). Pra QUALQUER outro tipo de usuário
  // (SECEX, CIEE, empresa, CPF sem autorização vinculada do tipo pedido),
  // o backend rejeita com 401/403/404 — comportamento correto. Nestes
  // casos, o frontend deve cair pro fluxo normal de login de empresa
  // (/tokens) ou pro próximo endpoint de estudante em vez de exibir erro.
  //
  // Parâmetros:
  //   config.endpointPath  → ex.: "/tca/login-estudante", "/tce/login-estudante"
  //   config.requestBody   → payload a enviar (cpf + senha)
  //   config.idField       → campo do JSON de resposta com o id da
  //                          autorização vinculada ("tca_id" ou "tce_id")
  //   config.storageKey    → chave do localStorage a persistir
  //                          ("estudante_tca_id" / "estudante_tce_id")
  //   config.rotaDestino   → rota a navegar quando houver id
  //                          (ex.: "/tca", "/tce")
  //   config.rotaFallback  → rota a navegar quando NÃO houver id
  //                          (mesma da rotaDestino, sem query string)
  //   config.tipoPrograma  → número a fixar em user.tipo_programa
  //                          (1 = TCA, 0 = TCE)
  //
  // Retorna um objeto com `kind`:
  //   'success'  → login-estudante teve sucesso (token salvo, navegação
  //                já disparada). Caller não deve prosseguir.
  //   'rejected' → login-estudante rejeitou explicitamente (401/403/404).
  //                Caller deve tentar o próximo endpoint ou cair pro
  //                /tokens de empresa.
  //   'error'    → erro de autenticação "real" (ex.: 400/500 com mensagem
  //                do backend, ou sem token no 200). Caller NÃO deve
  //                prosseguir; o erro já foi exibido.
  //   'network'  → erro de rede. Caller decide o que fazer.
  const tentarLoginEstudante = async (config, requestBody) => {
    const {
      endpointPath,
      idField,
      storageKey, // mantido na config (compat); a persistência do id
                  // é feita via `storageKeyFinal` calculado por (tipoPrograma, id).
      rotaDestino,
      rotaFallback,
      tipoPrograma,
    } = config;
    void storageKey;
    try {
      const response = await fetch(`${API_BASE_URL}${endpointPath}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
      });

      const data = await response.json().catch(() => null);

      // Quando o backend rejeita explicitamente (401/403/404), o usuário
      // não tem autorização do tipo pedido. Caller decide se tenta o
      // próximo endpoint de estudante ou cai pro /tokens de empresa.
      if (
        response.status === 401 ||
        response.status === 403 ||
        response.status === 404
      ) {        if (response.status === 404) {
          console.error(
            `Login ${endpointPath} retornou 404`,
            data,
          );
        }        return { kind: "rejected" };
      }

      if (!response.ok) {
        const msg = data?.errors?.[0] || data?.message
          || `Erro ao fazer login do estudante (${endpointPath}).`;
        setErrors({ user: msg });
        return { kind: "error" };
      }

      if (data?.token) {
        // SEMPRE prioridade: se o endpoint de estudante retornou 200,
        // é o caminho certo pro estudante. Mesmo que /tokens tenha
        // chegado antes e já setado navigatedRef (cenário: o CPF do
        // estudante TAMBÉM existe como usuário de empresa), o login
        // do estudante sobrescreve com o id correto.
        navigatedRef.current = true;
        localStorage.setItem("token", data.token);
        const decoded = jwtDecode(data.token);

        // O backend pode devolver o usuário em qualquer destas chaves:
        // response.user, response.usuario, data.user, data.usuario (varia
        // entre versões da API).
        const usuario =
          data?.user || data?.usuario || data?.User || data?.Usuario || null;

        // Decisão de destino AGORA é exclusivamente pelo par
        // (tipoPrograma, idAutorizacao) — nunca por
        // `decoded.tipo === "estudante"` nem por `acesso_autorizacao`.
        // Fontes, em ordem de prioridade:
        //   1) data.tipo_programa
        //   2) usuario?.tipo_programa  (response.user / response.usuario)
        //   3) decoded?.tipo_programa   (JWT)
        //   4) tipoPrograma             (fallback estrutural: endpoint chamado)
        const tipoProgramaResp = Number(
          data?.tipo_programa ??
          usuario?.tipo_programa ??
          decoded?.tipo_programa ??
          tipoPrograma
        );

        // Id da autorização (TCA ou TCE) — análogo ao tipoPrograma:
        // data > usuario > decoded. O id do estudante é separado.
        const tcaIdResp =
          data?.tca_id ?? usuario?.tca_id ?? decoded?.tca_id ?? null;
        const tceIdResp =
          data?.tce_id ?? usuario?.tce_id ?? decoded?.tce_id ?? null;
        const autorizacaoGenerica =
          data?.autorizacao_id ??
          usuario?.autorizacao_id ??
          data?.[idField] ??
          data?.id_tce ??
          data?.id_tca ??
          null;

        // Usuário persistido: prioriza `usuario` que veio no payload.
        const userData = usuario
          ? {
              ...decoded,
              ...usuario,
              acesso_autorizacao: Number(
                usuario?.acesso_autorizacao ??
                decoded?.acesso_autorizacao ??
                1
              ),
              tipo_programa: Number.isFinite(tipoProgramaResp)
                ? tipoProgramaResp
                : (decoded?.tipo_programa ?? tipoPrograma ?? null),
            }
          : {
              ...decoded,
              acesso_autorizacao: Number(decoded?.acesso_autorizacao ?? 1),
              tipo_programa: Number.isFinite(tipoProgramaResp)
                ? tipoProgramaResp
                : (decoded?.tipo_programa ?? tipoPrograma ?? null),
            };
        localStorage.setItem("user", JSON.stringify(userData));
        if (decoded?.id != null) {
          localStorage.setItem("estudante_id", String(decoded.id));
        }

        // Decisão final de navegação: par (tipoPrograma, idAutorizacao).
        let destino = null;
        let idEscolhido = null;
        let storageKeyFinal = null;

        if (tipoProgramaResp === 1 && (tcaIdResp || autorizacaoGenerica)) {
          destino = `/tca?id=${tcaIdResp || autorizacaoGenerica}`;
          idEscolhido = tcaIdResp || autorizacaoGenerica;
          storageKeyFinal = "estudante_tca_id";
        } else if (tipoProgramaResp === 0 && (tceIdResp || autorizacaoGenerica)) {
          destino = `/tce?id=${tceIdResp || autorizacaoGenerica}`;
          idEscolhido = tceIdResp || autorizacaoGenerica;
          storageKeyFinal = "estudante_tce_id";
        } else if (tipoProgramaResp === 2) {
          // TCA-Empregador. Rota sem id — a página busca o próprio.
          destino = rotaFallback || "/tca-empregador";
        } else {
          // Sem id de autorização conhecido: cai na rota padrão do
          // programa (TCA/TCE já tratam "Aguardando Empresa" nesse caso).
          destino = rotaFallback || rotaDestino;
        }

        // Log estruturado (Tarefa login-estudante 2026-09-23).
        console.log("[LOGIN AUTORIZACAO]", {
          endpoint: endpointPath,
          status: response.status,
          tca_id: tcaIdResp,
          tce_id: tceIdResp,
          tipo_programa: tipoProgramaResp,
          destino,
        });

        if (idEscolhido && storageKeyFinal) {
          localStorage.setItem(storageKeyFinal, String(idEscolhido));
        }

        // Navegação: a única navegação após o login. Não há
        // navegação posterior porque limpamos as chaves no início
        // de entrar() e o EmpresaRouteGuard só age em rotas liberadas.
        navigate(destino);
        return { kind: "success" };
      }

      const msg = data?.errors?.[0]
        || `Erro ao fazer login do estudante (${endpointPath}).`;
      setErrors({ user: msg });
      return { kind: "error" };
    } catch (err) {
      console.error(`Erro no login do Estudante (${endpointPath}):`, err);
      // Erro de rede — deixa o caller decidir o que fazer.
      return { kind: 'network' };
    }
  };

  // Login de empresa via /tokens. Retorna { kind } igual ao de estudante:
  //   'success' → navegou (já salvou token, localStorage, etc.)
  //   'error'   → backend respondeu com erro já tratado (401/403/400/5xx/sem token)
  //   'network' → fetch caiu (queda de rede)
  const tentarLoginEmpresa = async (usuarioLimpo, senha) => {
    const requestBody = { usuario: usuarioLimpo, senha };
    let response = null;
    let data = null;

    try {
      response = await fetch(API_ENDPOINTS.inicial_tokens, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestBody),
      });
    } catch (err) {
      console.error("Erro de rede ao chamar /tokens:", err);
      setErrors({ user: "Erro ao conectar com o servidor." });
      return { kind: "error" };
    }

    try {
      data = await response.json().catch(() => null);
    } catch (e) {
      data = null;
    }

    console.log("Login response", response && response.status, data);

    if (!response) {
      setErrors({ user: "Sem resposta do servidor." });
      return { kind: "error" };
    }

    if (response.status === 401) {
      console.error("Login 401 Unauthorized");
      const userMsg =
        (data && (data.message || (data.errors && data.errors[0]))) ||
        "Credenciais inválidas ou acesso não autorizado.";
      setErrors({ user: userMsg });
      return { kind: "error" };
    }

    // Tarefa B — Login 403 (acesso negado por regra de negócio).
    // NUNCA redirecionar para /login: já estamos nele.
    // Apenas exibir a mensagem retornada pelo backend.
    if (response.status === 403) {
      console.warn("Login 403 Forbidden (acesso negado por política)");
      const userMsg =
        (data && (data.message || (data.errors && data.errors[0]))) ||
        "Usuário sem permissão de acesso. Contate o administrador.";
      setErrors({ user: userMsg });
      return { kind: "error" };
    }

    if (response.status === 400) {
      let msg = "Requisição inválida (400). Verifique os campos.";
      if (data) {
        if (data.message) msg = data.message;
        else if (Array.isArray(data.errors)) {
          msg = data.errors
            .map((e) => (e && (e.message || e.msg)) || e)
            .join("; ");
        } else {
          try {
            msg = JSON.stringify(data);
          } catch (e) {
            /* ignore */
          }
        }
      }
      setErrors({ user: msg });
      return { kind: "error" };
    }

    if (response.status >= 500) {
      const msg =
        (data &&
          (data.message ||
            (data.errors && data.errors[0]) ||
            JSON.stringify(data))) ||
        "Erro interno do servidor (500). Verifique os logs do backend.";
      setErrors({ user: `Erro do servidor: ${msg}` });
      console.error("Servidor 5xx body:", data);
      return { kind: "error" };
    }

    if (response.ok && data && data.token) {
      // Se o /tce/login-estudante já navegou antes da gente, aborta
      // silenciosamente. Sem essa guarda, o token do /tokens SOBRESCREVE
      // o token do estudante e a rota vai para /tce SEM ?id=86, fazendo
      // o estudante cair na tela "Nenhuma autorização vinculada".
      if (navigatedRef.current) {
        return { kind: "rejected" };
      }
      navigatedRef.current = true;
      localStorage.setItem("token", data.token);
      const decoded = jwtDecode(data.token);
      const userData = {
        ...decoded,
        acesso_autorizacao: decoded.acesso_autorizacao ?? 0,
        tipo_programa:
          decoded.tipo === "usuario_ciee"
            ? null
            : (decoded.tipo_programa ?? null),
      };
      localStorage.setItem("user", JSON.stringify(userData));
      console.log("User logado:", userData);
      localStorage.setItem("empresa_id", decoded.empresa_id);

      if (decoded.tipo === "usuario_ciee") {
        localStorage.setItem("empresa_id", decoded.empresa_id || "");
        navigate("/dashboard");
        return { kind: "success" };
      }

      const tipoPrograma = Number(userData.tipo_programa);

      if (tipoPrograma === 0) {
        navigate("/tce");
        return { kind: "success" };
      }

      if (tipoPrograma === 1) {
        navigate("/tca");
        return { kind: "success" };
      }

      if (tipoPrograma === 2) {
        navigate("/tca-empregador");
        return { kind: "success" };
      }

      setErrors({
        user: "Tipo de programa inválido.",
      });
      return { kind: "error" };
    }

    setErrors({
      user: data && data.errors ? data.errors[0] : "Erro ao fazer login.",
    });
    return { kind: "error" };
  };

  const entrar = async (e) => {
    e.preventDefault();

    // Bloqueia re-submissão enquanto a request está em voo.
    if (loading) return;
    setErrors({});
    setLoading(true);
    // Reseta a flag de navegação a cada tentativa de login.
    navigatedRef.current = false;

    // Limpa defensivamente qualquer lixo do ESTUDANTE que tenha sobrado
    // da sessão anterior. Cenário típico: o usuário logou como estudante,
    // não clicou num logout que limpa TUDO (ex.: o Sair do Sidebar só
    // removia token/user/empresa_id), e agora vai logar como empresa.
    // Sem essa limpeza, campos como `estudante_id` aparecem pré-preenchidos
    // em formulários que checam essa chave.
    limparSessaoEstudante();

    // Limpeza adicional exigida pela Tarefa login-estudante (2026-09-23):
    // remoção explícita das chaves dos dois fluxos antes de uma nova
    // tentativa. Garante que um login intermediário (ex.: browser que
    // ficou com lixo de sessão prévia) não influencie a próxima decisão
    // de destino, que é 100% derivada da resposta do backend.
    try {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      localStorage.removeItem("estudante_tca_id");
      localStorage.removeItem("estudante_tce_id");
      localStorage.removeItem("estudante_id");
      localStorage.removeItem("empresa_id");
    } catch (_) {
      /* localStorage indisponível */
    }

    try {
      let newErrors = {};

      const usuarioLimpo = usuario.replace(/\D/g, "");

      if (!usuarioLimpo) {
        newErrors.usuario = "Informe CPF ou Código da Empresa";
      }

      if (!senha) {
        newErrors.senha = "Senha em branco!";
      }

      if (usuarioLimpo) {
        if (usuarioLimpo.length === 11) {
          if (!validarCPF(usuarioLimpo)) {
            newErrors.usuario = "CPF inválido!";
          }
        } else if (usuarioLimpo.length > 6) {
          newErrors.usuario = "Código da empresa inválido";
        }
      }

      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        return;
      }

      // Fluxo CPF (estudante): ordem explícita TCA → TCE → empresa.
      //
      // Por que TCA primeiro agora?
      //   O cenário atual é: a empresa usa o MESMO CPF para criar uma
      //   TCE e um TCA. O endpoint /tce/login-estudante pode devolver o
      //   id da TCE antiga (já preenchida) e fazer o estudante cair na
      //   tela da TCE antiga em vez do TCA mais recente. Para evitar isso,
      //   chamamos primeiro /tca/login-estudante. SÓ se ele devolver 404
      //   (não há autorização TCA vinculada a este CPF) caímos pra
      //   /tce/login-estudante. Se AMBOS derem 404, cai pro /tokens
      //   (login de empresa).
      //
      // A decisão de destino é tomada dentro de tentarLoginEstudante,
      // exclusivamente pelo par (tipoPrograma, idAutorizacao) devolvido
      // pelo backend — nunca por `tipo === "estudante"` ou
      // `tipo === "estudante"`.
      //
      // Se /tca/login-estudante retornar 200, ele JAMAIS será seguido
      // de uma chamada a /tce/login-estudante.
      if (usuarioLimpo.length === 11 && validarCPF(usuarioLimpo)) {
        const tcaResult = await tentarLoginEstudante(
          {
            endpointPath: '/tca/login-estudante',
            idField: 'tca_id',
            storageKey: 'estudante_tca_id',
            rotaDestino: '/tca',
            rotaFallback: '/tca',
            tipoPrograma: 1,
          },
          { cpf: usuarioLimpo, senha },
        );

        // Sucesso do /tca/login-estudante → já navegou pro TCA.
        if (tcaResult?.kind === 'success') return;
        // Erro de autenticação real (400/500 com msg do backend) →
        // mostrar erro e parar; não tenta os próximos.
        if (tcaResult?.kind === 'error') return;
        // 'rejected' (401/403/404) ou 'network' → tenta o /tce/login-estudante.

        const tceResult = await tentarLoginEstudante(
          {
            endpointPath: '/tce/login-estudante',
            idField: 'tce_id',
            storageKey: 'estudante_tce_id',
            rotaDestino: '/tce',
            rotaFallback: '/tce',
            tipoPrograma: 0,
          },
          { cpf: usuarioLimpo, senha },
        );

        if (tceResult?.kind === 'success') return;
        if (tceResult?.kind === 'error') return;
        // 'rejected' / 'network' → não é estagiário; cai pro /tokens
        // de empresa. (Pode ser que o CPF seja de uma empresa.)
        await tentarLoginEmpresa(usuarioLimpo, senha);
        return;
      }

      // Código de empresa (≤6 dígitos): fluxo direto, sem overhead dos
      // endpoints de estudante.
      await tentarLoginEmpresa(usuarioLimpo, senha);
    } catch (err) {
      console.error("Erro na requisição:", err);
      setErrors({ user: "Erro ao conectar com o servidor." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container" id="main-container">
      <form className="form-inicial login-form" onSubmit={entrar}>
        <div id="social_media">
          <img src="/logociee.png" alt="Logo" />
        </div>

        <div className="form_header">
          <h1>Login</h1>
        </div>

        <div id="inputs">
          <div className="input-box">
            <label htmlFor="usuario">CPF ou Código da Empresa</label>
            <div className="input-field">
              <i className="fa-solid fa-user"></i>
              <input
                type="text"
                id="usuario"
                name="usuario"
                className="input-tca"
                placeholder="000.000.000-00"
                maxLength="14"
                autoComplete="username"
                value={usuario}
                onChange={handleUsuarioChange}
                disabled={loading}
              />
            </div>
            <span className="error-message">{errors.usuario}</span>
          </div>

          <div className="input-box">
            <label htmlFor="senha">Senha</label>
            <div className="input-field">
              <i className="fa-solid fa-key"></i>
              <input
                type="password"
                id="senha"
                name="senha"
                placeholder="Senha"
                className="input-tca"
                autoComplete="current-password"
                value={senha}
                onChange={(e) => setSenha(e.target.value)}
                disabled={loading}
              />
            </div>
            <span className="error-message">{errors.senha}</span>
          </div>
        </div>

        <div id="user-error">{errors.user}</div>

        <button
          className={`login${loading ? " login--loading" : ""}`}
          type="submit"
          disabled={loading}
          aria-busy={loading}
        >
          {loading ? (
            <>
              <span className="login__spinner" aria-hidden="true" />
              <span>Acessando...</span>
            </>
          ) : (
            "Acessar"
          )}
        </button>
      </form>
    </div>
  );
};

export default Inicial;
